import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { constants as styleConstants } from "@/styles/config";
import {
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import { listCandidateProfiles } from "@/services/candidateProfiles";

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}

export default function CandidateProfilesDashboard() {
  const history = useHistory();

  const [profiles, setProfiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [highPotential, setHighPotential] = useState(false);
  const [hiringManager, setHiringManager] = useState(false);
  const [loading, setLoading] = useState(false);
  const [warning, setWarning] = useState(null);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    setWarning(null);
    try {
      const params = {
        page: page + 1,
        pageSize,
        ...(highPotential ? { highPotential: "true" } : {}),
        ...(hiringManager ? { hiringManager: "true" } : {})
      };
      const res = await listCandidateProfiles(params);
      setProfiles(res.data.data ?? []);
      setTotal(res.data.total ?? 0);
      if (res.data.warning) setWarning(res.data.warning);
    } catch (err) {
      console.error("Failed to load candidate profiles:", err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, highPotential, hiringManager]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const handleRowClick = (profile) => {
    history.push(`/variner/candidate-profiles/${profile.candidate_id}`);
  };

  const handlePageChange = (_, newPage) => setPage(newPage);

  const handleRowsPerPageChange = (e) => {
    setPageSize(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleHighPotentialChange = (e) => {
    setHighPotential(e.target.checked);
    setPage(0);
  };

  const handleHiringManagerChange = (e) => {
    setHiringManager(e.target.checked);
    setPage(0);
  };

  return (
    <Box p={4} ml={`${styleConstants.desktopDrawerClosedWidth}px`}>
      <Typography variant="h5" fontWeight={600} mb={3}>
        Candidate Profiles
      </Typography>

      <Box display="flex" gap={3} mb={3} alignItems="center">
        <FormControlLabel
          control={
            <Switch checked={highPotential} onChange={handleHighPotentialChange} color="primary" />
          }
          label={
            <Box display="flex" alignItems="center" gap={0.5}>
              <StarIcon fontSize="small" sx={{ color: "#f59e0b" }} />
              High Potential only
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Switch checked={hiringManager} onChange={handleHiringManagerChange} color="primary" />
          }
          label={
            <Box display="flex" alignItems="center" gap={0.5}>
              <BusinessCenterIcon fontSize="small" sx={{ color: "#6366f1" }} />
              Hiring Manager only
            </Box>
          }
        />
        {warning && (
          <Typography variant="caption" color="warning.main">
            ⚠ {warning}
          </Typography>
        )}
      </Box>

      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Title / Role</TableCell>
              <TableCell>Flags</TableCell>
              <TableCell>Last Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : profiles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">No candidates found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              profiles.map((p) => {
                const bh = p.bullhorn;
                const name =
                  bh?.firstName || bh?.lastName
                    ? `${bh.firstName ?? ""} ${bh.lastName ?? ""}`.trim()
                    : p.candidate_id;
                const location =
                  bh?.currentCity && bh?.currentState
                    ? `${bh.currentCity}, ${bh.currentState}`
                    : bh?.currentState ?? "—";

                return (
                  <TableRow
                    key={p.id}
                    hover
                    onClick={() => handleRowClick(p)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{bh?.email ?? "—"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{location}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{bh?.occupation ?? "—"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        {p.high_potential && (
                          <Chip
                            icon={<StarIcon />}
                            label="HP"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                        {p.hiring_manager && (
                          <Chip
                            icon={<BusinessCenterIcon />}
                            label="HM"
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(p.updated_at)}</Typography>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={pageSize}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Box>
  );
}
